import React, { useState, useEffect } from 'react';
import { FiMusic, FiUpload, FiX, FiPlay, FiCheck, FiTrash2 } from 'react-icons/fi';
import { API_UPLOAD_URL, UPLOAD_CONFIG, normalizeMediaUrl } from '../../config/apiConfig';
import '../ScrollStyles.css';

/**
 * Componente para upload de música (MP3)
 */
export default function MusicUploader({ 
  currentMusic, 
  onMusicSelect, 
  title = "Música de Fundo",
  cardTitle = "",
  cardHint = ""
}) {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState('');
  const [musicList, setMusicList] = useState([]);
  const [loadingList, setLoadingList] = useState(false);

  // Carregar lista de músicas
  useEffect(() => {
    loadMusicList();
  }, []);

  async function loadMusicList() {
    setLoadingList(true);
    setError('');
    try {
      const apiUrl = API_UPLOAD_URL.replace('/upload.php', '/list-images.php');
      const response = await fetch(apiUrl);
      const data = await response.json();
      
      if (data.success) {
        // Filtrar apenas arquivos MP3 (excluir pasta packs)
        const mp3Files = (data.data || []).filter(file => {
          const isMp3 = file.filename.toLowerCase().endsWith('.mp3');
          
          // EXCLUIR arquivos da pasta packs (galeria separada!)
          const path = file.path || file.filename;
          const isPack = path.includes('packs/');
          
          return isMp3 && !isPack;
        });
        setMusicList(mp3Files);
      } else {
        setError('Erro ao carregar lista de músicas.');
      }
    } catch (err) {
      console.error('Erro ao carregar músicas:', err);
      setError('Erro de conexão ao carregar músicas.');
    } finally {
      setLoadingList(false);
    }
  }

  async function handleMusicUpload(event) {
    const file = event.target.files[0];
    if (!file) return;

    // Validação de tipo
    if (!file.type.includes('audio/mpeg') && !file.name.toLowerCase().endsWith('.mp3')) {
      setError('Apenas arquivos MP3 são permitidos.');
      return;
    }

    // Validação de tamanho (10MB)
    if (file.size > 10 * 1024 * 1024) {
      setError('Arquivo muito grande. Máximo: 10MB.');
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setError('');

    try {
      const formData = new FormData();
      formData.append('image', file);
      
      const response = await fetch(API_UPLOAD_URL, {
        method: 'POST',
        body: formData,
      });

      const data = await response.json();

      if (data.success) {
        setUploadProgress(100);
        onMusicSelect(data.data.url);
        // Atualizar lista em tempo real
        loadMusicList();
      } else {
        setError(data.message || 'Erro ao fazer upload da música.');
      }
    } catch (err) {
      console.error('Erro no upload:', err);
      setError('Erro de conexão com o servidor de upload.');
    } finally {
      setUploading(false);
      setTimeout(() => setUploadProgress(0), 2000);
    }
  }

  function handleSelectMusic(musicUrl) {
    onMusicSelect(normalizeMediaUrl(musicUrl));
  }

  function handleRemoveMusic() {
    onMusicSelect('');
  }

  async function handleDeleteFile(filename, fileUrl) {
    // Confirmação
    if (!window.confirm(`Tem certeza que deseja excluir "${filename}"?`)) {
      return;
    }

    try {
      const deleteUrl = API_UPLOAD_URL.replace('/upload.php', '/delete.php');
      const response = await fetch(deleteUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ filename }),
      });

      const data = await response.json();

      if (data.success) {
        // Se a música deletada era a selecionada, limpar seleção
        if (currentMusic === fileUrl) {
          onMusicSelect('');
        }
        // Atualizar lista
        loadMusicList();
      } else {
        setError(data.message || 'Erro ao excluir arquivo.');
      }
    } catch (err) {
      console.error('Erro ao excluir:', err);
      setError('Erro ao excluir arquivo.');
    }
  }

  return (
    <div style={styles.container}>
      {/* Header do Card */}
      {cardTitle && (
        <div style={styles.cardHeader}>
          <div style={styles.cardHeaderLeft}>
            <div style={styles.cardTitle}>{cardTitle}</div>
            <div style={styles.cardHint}>{cardHint}</div>
          </div>
          <div style={styles.cardHeaderRight}>
            <input
              type="file"
              id={`upload-${title}`}
              accept="audio/mpeg,.mp3"
              onChange={handleMusicUpload}
              style={styles.fileInput}
              disabled={uploading}
            />
            <label htmlFor={`upload-${title}`} style={styles.uploadButtonCard}>
              <FiUpload size={14} />
              {uploading ? 'Enviando...' : 'Upload MP3'}
            </label>
          </div>
        </div>
      )}

      {error && (
        <div style={styles.error}>
          {error}
        </div>
      )}

      {/* Barra de progresso */}
      {uploading && (
        <div style={styles.progressBar}>
          <div style={{...styles.progressFill, width: `${uploadProgress}%`}}></div>
        </div>
      )}

      {/* Lista de Músicas */}
      <div style={styles.musicMode}>
        <div style={styles.musicHeader}>
          <FiMusic size={14} style={{marginRight: 6}} /> Músicas ({musicList.length})
        </div>
        
        {loadingList ? (
          <div style={styles.loading}>Carregando músicas...</div>
        ) : musicList.length === 0 ? (
          <div style={styles.empty}>
            <FiMusic size={32} style={{ opacity: 0.3 }} />
            <p>Nenhuma música disponível ainda.</p>
          </div>
        ) : (
          <div style={styles.musicListContainer} className="scroll-container">
            <div style={styles.musicList}>
              {musicList.map((music, index) => (
                <div
                  key={index}
                  style={{
                    ...styles.musicItem,
                    ...(normalizeMediaUrl(currentMusic) === normalizeMediaUrl(music.url) ? styles.musicItemSelected : {})
                  }}
                  onClick={() => handleSelectMusic(music.url)}
                >
                  <div style={styles.musicIcon}>
                    <FiMusic size={20} />
                  </div>
                  <div style={styles.musicInfo}>
                    <div style={styles.musicName}>{music.filename}</div>
                    <div style={styles.musicSize}>{(music.size / 1024 / 1024).toFixed(2)} MB</div>
                  </div>
                  {currentMusic === music.url && (
                    <div style={styles.selectedBadge}>
                      <FiCheck size={16} />
                    </div>
                  )}
                  {/* Botão de excluir */}
                  <button
                    className="delete-btn"
                    style={styles.deleteButton}
                    onClick={(e) => {
                      e.stopPropagation();
                      // Usar path se disponível, senão filename
                      const filePath = music.path || music.filename;
                      handleDeleteFile(filePath, music.url);
                    }}
                    title="Excluir música"
                  >
                    <FiTrash2 size={16} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Música selecionada */}
        {currentMusic && (
          <div style={styles.currentMusic}>
            <div style={styles.currentMusicLabel}>
              ✓ Música Ativa
            </div>
            <button onClick={handleRemoveMusic} style={styles.removeButton}>
              <FiX size={16} /> Remover (usar padrão)
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 12,
    paddingBottom: 12,
    borderBottom: '2px solid #e8e9ef',
  },
  cardHeaderLeft: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  cardTitle: {
    fontSize: 16,
    fontWeight: 700,
    color: '#333',
  },
  cardHint: {
    fontSize: 12,
    color: '#666',
  },
  cardHeaderRight: {
    display: 'flex',
    alignItems: 'center',
  },
  uploadButtonCard: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '6px 12px',
    background: '#1f6feb',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.2s',
    userSelect: 'none',
  },
  error: {
    padding: 12,
    background: '#ffe8e6',
    border: '1px solid #ffcbc4',
    borderRadius: 10,
    color: '#9a3c2f',
    fontSize: 14,
    fontWeight: 600,
  },
  fileInput: {
    display: 'none',
  },
  progressBar: {
    width: '100%',
    height: 6,
    background: '#e8e9ef',
    borderRadius: 3,
    overflow: 'hidden',
    marginBottom: 12,
  },
  progressFill: {
    height: '100%',
    background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
    transition: 'width 0.3s ease',
  },
  musicMode: {
    minHeight: 200,
  },
  musicHeader: {
    display: 'flex',
    alignItems: 'center',
    fontSize: 13,
    fontWeight: 600,
    color: '#444',
    marginBottom: 12,
    paddingBottom: 8,
    borderBottom: '1px solid #e8e9ef',
  },
  loading: {
    textAlign: 'center',
    padding: 40,
    color: '#999',
    fontSize: 14,
  },
  empty: {
    textAlign: 'center',
    padding: 40,
    color: '#999',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 12,
  },
  musicListContainer: {
    maxHeight: 400,
    overflowY: 'auto',
    overflowX: 'hidden',
    paddingRight: 8,
    // Scroll customizado
    scrollbarWidth: 'thin',
    scrollbarColor: '#1f6feb #f0f0f0',
  },
  musicList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  musicItem: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: 12,
    border: '2px solid #e8e9ef',
    borderRadius: 10,
    cursor: 'pointer',
    transition: 'all 0.2s',
    background: '#fff',
  },
  musicItemSelected: {
    borderColor: '#1f6feb',
    background: '#f0f6ff',
    boxShadow: '0 0 0 3px rgba(31, 111, 235, 0.2)',
  },
  musicIcon: {
    width: 40,
    height: 40,
    borderRadius: 8,
    background: '#f1f4ff',
    color: '#1f6feb',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  musicInfo: {
    flex: 1,
  },
  musicName: {
    fontSize: 14,
    color: '#333',
    fontWeight: 600,
    marginBottom: 2,
  },
  musicSize: {
    fontSize: 12,
    color: '#666',
  },
  selectedBadge: {
    background: '#1f6feb',
    color: '#fff',
    borderRadius: '50%',
    width: 28,
    height: 28,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    zIndex: 2,
  },
  deleteButton: {
    position: 'absolute',
    right: 12,
    background: '#dc3545',
    color: '#fff',
    border: 'none',
    borderRadius: '50%',
    width: 32,
    height: 32,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    opacity: 0,
    transition: 'opacity 0.2s, transform 0.2s',
    zIndex: 3,
  },
  currentMusic: {
    marginTop: 16,
    padding: 12,
    background: '#eaffea',
    border: '1px solid #b7efb7',
    borderRadius: 10,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  currentMusicLabel: {
    fontSize: 13,
    fontWeight: 600,
    color: '#1a7f1a',
  },
  removeButton: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '6px 12px',
    background: '#fff',
    border: '1px solid #ddd',
    borderRadius: 8,
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
    color: '#666',
  },
};
